import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { useB2BAuth } from "@/lib/b2bAuth";
import { Input } from "@/components/ui/input";

export default function B2BLoginPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { login } = useB2BAuth();
  const [form, setForm] = useState({ email: params.get("email") || "", password: "", remember_me: false });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(form.email, form.password, form.remember_me);
    setLoading(false);
    if (res.ok) {
      toast.success("Accesso effettuato");
      if (res.user?.must_change_password) toast.info("Ti invitiamo a cambiare password dal profilo.");
      nav("/area-b2b/dashboard", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="b2b-login-page">
      <header className="px-6 lg:px-10 py-6 border-b border-soft bg-white">
        <div className="mx-auto max-w-[1400px] flex justify-between items-center">
          <Logo />
          <Link to="/" className="text-sm text-brand link-underline">Torna al sito</Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <section className="lg:col-span-6">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-4">Area Riservata</div>
          <h1 className="font-display text-5xl md:text-6xl text-brand leading-[1.02]">
            Area Riservata B2B
          </h1>
          <p className="mt-6 text-lg text-muted-ink max-w-xl">
            L'Area Riservata BioNutrix è dedicata ai professionisti e ai partner commerciali autorizzati.
            Accedi per consultare e scaricare certificazioni, schede tecniche, cataloghi e altra
            documentazione riservata.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
            <Link
              to="/area-b2b/richiedi-accesso"
              className="block border border-soft bg-white p-5 hover:border-brand transition-colors"
              data-testid="b2b-request-access-link"
            >
              <div className="text-xs uppercase tracking-editorial text-muted-ink mb-1">Non hai ancora un accesso?</div>
              <div className="font-display text-xl text-brand">Richiedi accesso <ArrowRight className="inline h-4 w-4" /></div>
            </Link>
            <Link
              to="/area-b2b/recupera-password"
              className="block border border-soft bg-white p-5 hover:border-brand transition-colors"
            >
              <div className="text-xs uppercase tracking-editorial text-muted-ink mb-1">Hai dimenticato la password?</div>
              <div className="font-display text-xl text-brand">Recupera password <ArrowRight className="inline h-4 w-4" /></div>
            </Link>
          </div>
          <p className="mt-8 text-sm text-muted-ink">
            Per assistenza: <a href="mailto:bionutrix.supplements@gmail.com" className="link-underline text-brand">bionutrix.supplements@gmail.com</a>
          </p>
        </section>

        <section className="lg:col-span-5 lg:col-start-8">
          <form onSubmit={submit} className="bg-white border border-soft p-8 space-y-4" data-testid="b2b-login-form">
            <h2 className="font-display text-2xl text-brand mb-2">Accedi</h2>
            <label className="block">
              <span className="text-xs uppercase tracking-editorial text-muted-ink block mb-1.5">Username o email</span>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-ink" />
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="pl-9 rounded-none border-soft bg-white"
                  data-testid="b2b-login-email"
                  autoComplete="email"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-editorial text-muted-ink block mb-1.5">Password</span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-ink" />
                <Input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className="pl-9 rounded-none border-soft bg-white"
                  data-testid="b2b-login-password"
                  autoComplete="current-password"
                />
              </div>
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-ink cursor-pointer">
              <input
                type="checkbox"
                checked={form.remember_me}
                onChange={(e) => setForm((f) => ({ ...f, remember_me: e.target.checked }))}
              />
              <span>Ricordami</span>
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="b2b-login-submit"
              className="w-full rounded-full bg-brand text-white py-3 text-sm hover:bg-[#0f2020] disabled:opacity-50 transition-colors"
            >
              {loading ? "…" : "Accedi"}
            </button>
            <div className="text-center">
              <Link to="/area-b2b/recupera-password" className="text-xs text-muted-ink link-underline">Password dimenticata?</Link>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

// ============= REQUEST ACCESS =============
export function B2BRequestAccessPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", professional_role: "", account_type: "reseller",
    vat_number: "", email: "", phone: "", city: "", province: "", website: "",
    message: "", motivation: "", privacy_consent: false, marketing_consent: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) { toast.error("Consenso privacy obbligatorio"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/b2b/access-request", form);
      toast.success(data.message);
      setTimeout(() => nav("/area-b2b"), 1500);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="b2b-request-page">
      <header className="px-6 lg:px-10 py-6 border-b border-soft bg-white">
        <div className="mx-auto max-w-[1400px] flex justify-between items-center">
          <Logo />
          <Link to="/area-b2b" className="text-sm text-brand link-underline">Torna al login</Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 lg:px-10 py-12 lg:py-16">
        <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">Nuovo accesso</div>
        <h1 className="font-display text-4xl md:text-5xl text-brand leading-tight mb-3">Richiedi accesso all'Area B2B</h1>
        <p className="text-muted-ink mb-8">Compila il modulo: la richiesta verrà valutata dal nostro team. Solo dopo l'approvazione riceverai le credenziali di accesso.</p>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white border border-soft p-6" data-testid="b2b-request-form">
          <Input required placeholder="Nome" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="rounded-none border-soft" />
          <Input required placeholder="Cognome" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="rounded-none border-soft" />
          <Input required placeholder="Azienda" value={form.company} onChange={(e) => set("company", e.target.value)} className="rounded-none border-soft md:col-span-2" />
          <Input placeholder="Ruolo professionale" value={form.professional_role} onChange={(e) => set("professional_role", e.target.value)} className="rounded-none border-soft" />
          <select value={form.account_type} onChange={(e) => set("account_type", e.target.value)} className="h-9 border border-soft bg-white px-3 text-sm rounded-none">
            <option value="reseller">Rivenditore</option>
            <option value="distributor">Distributore</option>
            <option value="vet">Veterinario</option>
            <option value="pharmacy">Farmacia</option>
            <option value="parapharmacy">Parafarmacia</option>
            <option value="pet_shop">Pet shop</option>
            <option value="agent">Agente commerciale</option>
            <option value="collaborator">Collaboratore</option>
            <option value="other">Altro</option>
          </select>
          <Input placeholder="Partita IVA" value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} className="rounded-none border-soft" />
          <Input placeholder="Telefono" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-none border-soft" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none border-soft md:col-span-2" />
          <Input placeholder="Città" value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-none border-soft" />
          <Input placeholder="Provincia" value={form.province} onChange={(e) => set("province", e.target.value)} className="rounded-none border-soft" />
          <Input placeholder="Sito web" value={form.website} onChange={(e) => set("website", e.target.value)} className="rounded-none border-soft md:col-span-2" />
          <textarea placeholder="Motivazione della richiesta" value={form.motivation} onChange={(e) => set("motivation", e.target.value)} rows={4} className="md:col-span-2 border border-soft bg-white p-3 text-sm rounded-none" />
          <label className="md:col-span-2 flex items-start gap-2 text-sm text-muted-ink cursor-pointer">
            <input type="checkbox" required checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} className="mt-1" />
            <span>Ho letto la Privacy Policy e acconsento al trattamento dei dati.</span>
          </label>
          <label className="md:col-span-2 flex items-start gap-2 text-sm text-muted-ink cursor-pointer">
            <input type="checkbox" checked={form.marketing_consent} onChange={(e) => set("marketing_consent", e.target.checked)} className="mt-1" />
            <span>Acconsento a ricevere comunicazioni commerciali.</span>
          </label>
          <button type="submit" disabled={loading} data-testid="b2b-request-submit" className="md:col-span-2 rounded-full bg-brand text-white px-8 py-3 text-sm hover:bg-[#0f2020] disabled:opacity-50">
            {loading ? "Invio in corso…" : "Invia richiesta"}
          </button>
        </form>
      </main>
    </div>
  );
}

// ============= FORGOT PASSWORD =============
export function B2BForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/b2b-auth/forgot-password", { email });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="b2b-forgot-page">
      <header className="px-6 lg:px-10 py-6 border-b border-soft bg-white">
        <div className="mx-auto max-w-[1400px] flex justify-between items-center">
          <Logo />
          <Link to="/area-b2b" className="text-sm text-brand link-underline">Torna al login</Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <h1 className="font-display text-4xl text-brand mb-3">Recupera password</h1>
          {sent ? (
            <div className="border border-soft bg-white p-6">
              <p className="text-brand mb-2">Richiesta ricevuta.</p>
              <p className="text-sm text-muted-ink">Se l'indirizzo è registrato riceverai a breve un'email con le istruzioni per reimpostare la password.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-muted-ink text-sm">Inserisci l'email associata al tuo account: ti invieremo un link temporaneo per impostare una nuova password.</p>
              <Input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-soft bg-white" data-testid="b2b-forgot-email" />
              <button type="submit" disabled={loading} data-testid="b2b-forgot-submit" className="w-full rounded-full bg-brand text-white py-3 text-sm hover:bg-[#0f2020] disabled:opacity-50">
                {loading ? "…" : "Invia link di recupero"}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

// ============= SET PASSWORD (magic link) =============
export function B2BSetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const token = params.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Le password non coincidono"); return; }
    if (pw.length < 8) { toast.error("Almeno 8 caratteri"); return; }
    setLoading(true);
    try {
      await api.post("/b2b-auth/set-password", { token, password: pw });
      toast.success("Password impostata. Ora puoi accedere.");
      setTimeout(() => nav("/area-b2b"), 1200);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="b2b-set-password-page">
      <header className="px-6 lg:px-10 py-6 border-b border-soft bg-white">
        <div className="mx-auto max-w-[1400px]"><Logo /></div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">Primo accesso</div>
          <h1 className="font-display text-4xl text-brand mb-6">Imposta la tua password</h1>
          {!token ? (
            <p className="text-destructive">Link non valido.</p>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-muted-ink">Scegli una password sicura di almeno 8 caratteri.</p>
              <Input type="password" required placeholder="Nuova password" value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-none border-soft bg-white" autoComplete="new-password" />
              <Input type="password" required placeholder="Conferma password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="rounded-none border-soft bg-white" autoComplete="new-password" />
              <button type="submit" disabled={loading} data-testid="b2b-set-password-submit" className="w-full rounded-full bg-brand text-white py-3 text-sm hover:bg-[#0f2020] disabled:opacity-50">
                {loading ? "…" : "Imposta password"}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
